import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HERO_VIDEO_URL, CALENDAR_URL } from "@/lib/site";

gsap.registerPlugin(ScrollTrigger);

const OVERLAYS = [
  { text: "Every business has a story to tell.", show: [0.02, 0.24] },
  { text: "We help you clarify and refine your message.", show: [0.28, 0.5] },
  { text: "And we amplify it to your local or national audience through Meta advertising.", show: [0.54, 0.72] },
];

export const VideoHero = () => {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const overlayRefs = useRef([]);
  const finalRef = useRef(null);
  const targetTime = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isMobile) return;
    const video = videoRef.current;
    if (!video) return;
    let raf;
    let duration = 0;

    const onMeta = () => { duration = video.duration || 0; setReady(true); };
    video.addEventListener("loadedmetadata", onMeta);
    if (video.readyState >= 1) onMeta();

    // Smoothly lerp the video time toward the scroll target
    const tick = () => {
      if (video.readyState >= 2 && duration) {
        const cur = video.currentTime;
        const next = cur + (targetTime.current - cur) * 0.18;
        if (Math.abs(next - cur) > 0.005) {
          try { video.currentTime = next; } catch (e) {}
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const ctx = gsap.context(() => {
      gsap.set(overlayRefs.current, { autoAlpha: 0, y: 14 });
      gsap.set(finalRef.current, { autoAlpha: 0, y: 18 });

      const st = ScrollTrigger.create({
        trigger: wrapRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
        pin: stageRef.current,
        anticipatePin: 1,
        onUpdate: (self) => {
          const p = self.progress;
          if (duration) targetTime.current = p * duration;
          OVERLAYS.forEach((o, i) => {
            const el = overlayRefs.current[i];
            if (!el) return;
            const [a, b] = o.show;
            const visible = p >= a && p <= b;
            gsap.to(el, { autoAlpha: visible ? 1 : 0, y: visible ? 0 : 14, duration: 0.5, overwrite: true });
          });
          const finalVisible = p >= 0.76;
          gsap.to(finalRef.current, { autoAlpha: finalVisible ? 1 : 0, y: finalVisible ? 0 : 18, duration: 0.6, overwrite: true });
        },
      });

      return () => st.kill();
    }, wrapRef);

    return () => {
      cancelAnimationFrame(raf);
      video.removeEventListener("loadedmetadata", onMeta);
      ctx.revert();
    };
  }, [isMobile]);

  if (isMobile) {
    return (
      <section className="relative bg-ink-900" data-testid="video-hero-mobile">
        <div className="relative h-[88vh] overflow-hidden flex items-end justify-center">
          <video
            src={HERO_VIDEO_URL}
            autoPlay muted loop playsInline preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-ink-900/30 to-ink-900" />
          <div className="relative z-10 text-center px-6 pb-16">
            <h2 className="font-display text-3xl sm:text-4xl text-cream tracking-wide text-glow">Clarify. Refine. Amplify.</h2>
            <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer" data-testid="video-hero-mobile-apply" className="inline-flex mt-7 label text-xs bg-gold text-ink-900 px-8 py-4 rounded-[2px]">Apply to Work With Us →</a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={wrapRef} className="relative h-[460vh] bg-ink-900" data-testid="video-hero">
      <div ref={stageRef} className="h-screen w-full overflow-hidden relative flex items-center justify-center">
        <video
          ref={videoRef}
          src={HERO_VIDEO_URL}
          muted playsInline preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-ink-900/25 pointer-events-none" />
        <div className="absolute inset-0 [background:radial-gradient(120%_90%_at_50%_50%,transparent_45%,rgba(10,8,4,0.7)_100%)] pointer-events-none" />

        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-parch label text-xs">Loading the story…</div>
        )}

        {OVERLAYS.map((o, i) => (
          <div
            key={i}
            ref={(el) => (overlayRefs.current[i] = el)}
            className="absolute inset-x-0 bottom-[16%] sm:bottom-[20%] text-center px-6 pointer-events-none"
          >
            <p className="font-display text-2xl sm:text-4xl lg:text-5xl text-cream max-w-4xl mx-auto leading-snug text-glow">{o.text}</p>
          </div>
        ))}

        <div ref={finalRef} className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-cream tracking-wide leading-[1.1] text-glow">
            Clarify. Refine. Amplify.
          </h2>
          <a
            href={CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-hero-apply"
            className="label text-xs sm:text-sm inline-flex items-center gap-2 mt-10 bg-gold text-ink-900 px-10 py-4 rounded-[2px] hover:bg-gold-light transition-all duration-300 shadow-[0_0_40px_rgba(201,146,58,0.4)]"
          >
            Apply to Work With Us <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoHero;
